<script setup lang="ts">
import type { ImageBlock } from '../../../model/assessments/interfaces'

import { AppText } from '@shared/ui/typography'

import { usePreviewContentImage } from '../../../model/assessments/preview-content.hook'
import { useAssessmentMediaSrc } from '../../../model/assessments/media-asset.hook'
import PreviewAuthorNote from './PreviewAuthorNote.vue'

/**
 * An image block: `asset.url` + `alt`. The `src` comes from `useAssessmentMediaSrc` — a path is
 * exchanged for a signed link, a URL passed through — which is still a read and calls no store
 * action (§2-1).
 *
 * An absent URL renders a labelled placeholder, not an `<img>` with an empty `src`: a broken-image
 * icon reads as failure when the real state is "not authored yet". So does an unsignable path.
 */
defineOptions({
  name: 'PreviewContentImage',
})

const props = defineProps<{
  block: ImageBlock
}>()

const {
  refUrl,
  refHasUrl,
  refAlt,
  refHasAlt,
} = usePreviewContentImage(() => props.block)

const {
  refSrc,
  refIsResolving,
  refError,
} = useAssessmentMediaSrc(() => refUrl.value)
</script>

<template>
  <div class="preview-content-image">
    <img
      v-if="refSrc"
      class="preview-content-image__img"
      :src="refSrc"
      :alt="refAlt"
    >

    <div
      v-else
      class="preview-content-image__placeholder"
    >
      <AppText
        name="body2"
        color="slate-500"
      >
        <template v-if="!refHasUrl">
          Image — no URL set yet.
        </template>
        <template v-else-if="refIsResolving">
          Image — loading…
        </template>
        <template v-else>
          Image — {{ refError ?? 'could not be loaded' }}
        </template>
      </AppText>
    </div>

    <!-- `alt` is what a kid using a screen reader hears, so an empty one is an authoring gap
         worth naming. The schema does not require it to be non-empty, so this is a note. -->
    <PreviewAuthorNote v-if="refHasUrl && !refHasAlt">
      this image has no alt text.
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewContentImage.scss" />
